import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Grid from './Grid';
import eventEmitter from '../utils/eventEmitter';
import withRouter from '../utils/withRouter';
import flags from '../utils/flags';
import { getPrimaryField } from '../utils/helper';


let _lastGet = Date.now();
let _lastID;


/**
 * Grid Component 
 * @module
 * @param {bool} [allColumns] - All colums
 * @param {string} [cancelText] - Additional cancel text
 * @param {func} [cellRender] - Cell render function
 * @param {bool} [cellSelect] - Cell select
 * @param {string} [className] - Class name
 * @param {array} [contextMenu] - Context menu
 * @param {array} data - Data
 * @param {string} [deleteText] - Additional delete text 
 * @param {any} [detail] - Detail
 * @param {number} [disabled] - Disabled
 * @param {string} [discardText] - Discard text * @param {bool} [editable] - Editable
 * @param {bool} [editableCell] - EditableCell
 * @param {func} [enterEdit] - Enter edit function
 * @param {func} [exitEdit] - Exit edit function
 * @param {object} [fixedFilter] - Fixed filter
 * @param {number} [hidden] - Hidden
 * @param {bool} [changeTabDisable] - Change tab disable
 * @param {object} [initFilter] - Init filter
 * @param {array} [initSort] - Init sort 
 * @param {bool} isLoading - Is loading
 * @param {bool} [modified] - Modified
 * @param {bool} [multiple] - Multiple
 * @param {func} [onAdd] - Function invoked on entry add
 * @param {func} [onCancel] - Function invoked on entry cancel
 * @param {func} [onContextMenuSelect] - On context menu select function
 * @param {func} [onCopy] - Function invoked on entry copy
 * @param {func} [onCopyAdditional] - On copy additional function
 * @param {func} [onDelete] - Function invoked on entry delete
 * @param {func} [onExpandChange] - On expand change function
 * @param {func} onGet - On get function
 * @param {func} onGetList - On get list function
 * @param {func} [onGetSchema] - On get schema function
 * @param {func} onChange - On change function
 * @param {func} [onChangeField] - On chage field function
 * @param {func} [onPaste] - On paste from clipboard function
 * @param {func} [onRowDoubleClick] - On row double click function
 * @param {func} [onSelect] - On select function
 * @param {func} [onSubmit] - On submit function
 * @param {func} [onUpdate] - On update function
 * @param {object} [parentProps] - Parent props
 * @param {string} primaryField - Primary field
 * @param {bool} [printable] - Printable
 * @param {bool} [rememberSelection] - Remember selection
 * @param {func} [rowProps] - Row props function
 * @param {array} schema - Grid schema
 * @param {string} [saveChangesDialogText] - SaveChangesDialog text
 * @param {array} [selected] - Selected entries
 * @param {bool} [showAbort] - Show abort button for async requests
 * @param {bool} [showToolbarButtons] - Show buttons with labels (not icons)  in toolbar
 * @param {string} [customToolbarBreadcrumb] - display custom breadcrumb in toolbar
 * @param {bool} [sortDialog] - Sort dialog in grid
 * @param {object} [tlac] - Metadata object for reports
 * @param {any} [toolbarActions] - Toolbar actions
 * @param {bool} [toolbarHidden] - Toolbar hidden
 * @param {number} total - Defines the total number of data items in all pages
 * @param {bool} [userFilter] - User filter
 * @param {string} [filterField] - Filter field
 * @param {func} [reducer] - Reducer
 */
class GridWrapper extends React.Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  onGet = async (id, fixedFilter = null) => {
    const { allColumns, data, onGet } = this.props;

    if (allColumns) {
      let val;
      if (id === 0) {
        val = null;
      }
      else {
        val = { Data: data.find(x => x[this.getPrimaryField()] === id) }
      }
      await onGet(val);
    }
    else {
      await onGet(id, fixedFilter);
    }
  }

  initDetail = () => {
    try {
      const { location, allColumns, withMeta } = this.props;
      const urlParams = new URLSearchParams(location.search);
      const id = urlParams.get('id');
      if (id && withMeta && !allColumns) {
        this.onGet(id);
        this.changeTab();
        urlParams.delete('id');
        urlParams.size > 0 ? this.props.navigate(`${location.pathname}?${urlParams.toString()}`) : this.props.navigate(location.pathname);
      }
    } catch { }
  }

  onAdd = async (event) => {
    if (!this.props.changeTabDisable) {
      this.changeTab();
    }
    if (_.isFunction(this.props.onSelect)) {
      this.props.onSelect([]);
    }
    await this.onGet(0, this.props.fixedFilter);
  }

  onCopy = async () => {
    this.props.onChangeField(this.getPrimaryField(), 0);
    if (this.props.onCopyAdditional) {
      await this.props.onCopyAdditional();
    }
    this.changeTab();
  }

  changeTab = () => {
    eventEmitter.changeTab(this.props.primaryField, 1);
  }

  getPrimaryField = () => {
    return getPrimaryField(this.props.primaryField);
  }

  onSelect = async (selected, isCopy = false, selectedAll) => {
    const { onSelect } = this.props;

    if (_.isFunction(onSelect)) {
      await onSelect(selected, isCopy, selectedAll);
    }

    if (selected.length === 1) {
      const id = selected[0][this.getPrimaryField()];
      const lastGet = Date.now();
      if (id !== _lastID || lastGet - _lastGet > 500) {
        _lastID = id;
        _lastGet = lastGet;
        await this.onGet(id);
      }
    }
    else {
      if (!isCopy && _lastID !== 0) {
        _lastID = 0;
        this.onGet(0);
      }
    }
  }

  onDelete = async (selected) => {
    const { onGetList, onDelete } = this.props;

    await onDelete(selected[this.getPrimaryField()]);
    await this.onGet(0);
    onGetList();
  }

  onCancel = async (selected) => {
    const { onGetList, onCancel } = this.props;
    const primaryField = this.getPrimaryField();

    await onCancel(selected[primaryField]);
    await this.onGet(selected[primaryField]);
    onGetList();
  }

  onChange = (filter, sort, page) => {
    const { onGetList, onChange } = this.props;
    onChange(filter, sort, page);
    onGetList();
  }

  getHidden = () => {
    let { hidden } = this.props;

    if (!this.props.printable) {
      hidden |= flags.PRINT;
    }

    if (!this.props.sortDialog) {
      hidden |= flags.SORT_DIALOG;
    }

    if (process.env.REACT_APP_NAME !== 'VISCO' && !this.props.reset) {
      hidden |= flags.RESET;
    }

    if (process.env.REACT_APP_NAME !== 'VISCO' && !this.props.deleteAll) {
      hidden |= flags.DELETEALL;
    }

    if (!this.props.editable) {
      hidden |= flags.EDIT;
    }

    if (!this.props.editableAll) {
      hidden |= flags.EDITALL;
    }

    if (!this.props.editableCell) {
      hidden |= flags.EDITCELL;
    } else if (this.props.data && this.props.data.find(item => item[this.getPrimaryField()] === 0)) {
      hidden |= flags.ADD | flags.COPY
    }

    if (!this.props.userFilter) {
      hidden |= flags.USER_FILTER;
    }

    return hidden;
  }

  getDisabled = () => {
    let { disabled } = this.props;

    if (process.env.REACT_APP_NAME !== 'VISCO' && !this.props.deleteAll) {
      disabled |= flags.DELETEALL;
    }

    return disabled;
  }

  render() {
    const hidden = this.getHidden();
    const disabled = this.getDisabled();

    return (
      <Grid
        ref={this.ref}
        actions={this.props.actions}
        allColumns={this.props.allColumns}
        cancelText={this.props.cancelText}
        cellRender={this.props.cellRender}
        cellSelect={this.props.cellSelect}
        className={this.props.className}
        contextMenu={this.props.contextMenu}
        data={this.props.data}
        deleteText={this.props.deleteText}
        detail={this.props.detail}
        disabled={disabled}
        discardText={this.props.discardText}
        editableCell={this.props.editableCell}
        enterEdit={this.props.enterEdit}
        exitEdit={this.props.exitEdit}
        hidden={hidden}
        filter={this.props.filter}
        fixedFilter={this.props.fixedFilter}
        onFilter={this.props.onFilter}
        page={this.props.page}
        customPrintProps={this.props.customPrintProps}
        initFilter={this.props.initFilter}
        initSort={this.props.initSort}
        isLoading={this.props.isLoading}
        modified={this.props.modified}
        multiple={this.props.multiple}
        noMeta={this.props.noMeta}
        withMeta={this.props.withMeta}
        oldData={this.props.oldData}
        onAbortChangesAll={this.props.onAbortChangesAll}
        onAbortChanges={this.props.onAbortChanges}
        onAdd={this.props.onAdd ? this.props.onAdd : this.onAdd}
        onCancel={this.onCancel}
        onConfirm={this.changeTab}
        onContextMenuSelect={this.props.onContextMenuSelect}
        onCopy={this.props.onCopy ? this.props.onCopy : this.onCopy}
        onCreate={this.props.onCreate}
        onDelete={this.onDelete}
        onExpandChange={this.props.onExpandChange}
        onGetSchema={this.props.onGetSchema}
        onChange={this.onChange}
        onItemChange={this.props.onItemChange}
        onInitDetail={this.initDetail}
        onPaste={this.props.onPaste}
        onPrint={this.props.onPrint}
        onRowDoubleClick={this.props.onRowDoubleClick}
        onSelect={this.onSelect}
        onSubmit={this.props.onSubmit}
        onUpdate={this.props.onUpdate}
        parentProps={this.props.parentProps}
        primaryField={this.props.primaryField}
        printItems={this.props.printItems}
        rememberSelection={this.props.rememberSelection}
        rowProps={this.props.rowProps}
        saveChangesDialogText={this.props.saveChangesDialogText}
        selected={this.props.selected}
        schema={this.props.schema}
        showAbort={this.props.showAbort}
        customToolbarBreadcrumb={this.props.customToolbarBreadcrumb}
        showToolbarButtons={this.props.showToolbarButtons}
        tlac={this.props.tlac}
        toolbarActions={this.props.toolbarActions}
        toolbarHidden={this.props.toolbarHidden}
        total={this.props.total}
        filterField={this.props.filterField}
        db={this.props.db}
        reducer={this.props.reducer}
        componentFactory={this.props.componentFactory}
      />
    )
  }

}

GridWrapper.propTypes = {
  allColumns: PropTypes.bool,
  cancelText: PropTypes.string,
  cellRender: PropTypes.func,
  cellSelect: PropTypes.bool,
  className: PropTypes.string,
  contextMenu: PropTypes.array,
  customPrintProps: PropTypes.object,
  customToolbarBreadcrumb: PropTypes.string,
  data: PropTypes.array.isRequired,
  deleteText: PropTypes.string,
  detail: PropTypes.any,
  disabled: PropTypes.number,
  editable: PropTypes.bool,
  editableCell: PropTypes.bool,
  enterEdit: PropTypes.func,
  exitEdit: PropTypes.func,
  filter: PropTypes.object,
  fixedFilter: PropTypes.object,
  onFilter: PropTypes.func,
  page: PropTypes.object,
  hidden: PropTypes.number,
  changeTabDisable: PropTypes.bool,
  initFilter: PropTypes.object,
  initSort: PropTypes.array,
  isLoading: PropTypes.bool.isRequired,
  modified: PropTypes.bool,
  multiple: PropTypes.bool,
  noMeta: PropTypes.bool,
  withMeta: PropTypes.bool,
  oldData: PropTypes.array,
  onAdd: PropTypes.func,
  onCancel: PropTypes.func,
  onContextMenuSelect: PropTypes.func,
  onCopy: PropTypes.func,
  onCopyAdditional: PropTypes.func,
  onDelete: PropTypes.func,
  onExpandChange: PropTypes.func,
  onGet: PropTypes.func.isRequired,
  onGetList: PropTypes.func.isRequired,
  onGetSchema: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onChangeField: PropTypes.func,
  onItemChange: PropTypes.func,
  onPaste: PropTypes.func,
  onPrint: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onSelect: PropTypes.func,
  onSubmit: PropTypes.func,
  onUpdate: PropTypes.func,
  parentProps: PropTypes.object,
  primaryField: PropTypes.string.isRequired,
  printItems: PropTypes.array,
  printable: PropTypes.bool,
  rememberSelection: PropTypes.bool,
  rowProps: PropTypes.func,
  saveChangesDialogText: PropTypes.string,
  selected: PropTypes.array,
  schema: PropTypes.array.isRequired,
  showAbort: PropTypes.bool,
  showToolbarButtons: PropTypes.bool,
  sortDialog: PropTypes.bool,
  tlac: PropTypes.object,
  toolbarActions: PropTypes.any,
  toolbarHidden: PropTypes.bool,
  total: PropTypes.number.isRequired,
  userFilter: PropTypes.bool,
  filterField: PropTypes.string,
  reducer: PropTypes.func,
  componentFactory: PropTypes.func,
}

export default withRouter(GridWrapper);